import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, mergeMap, switchMap, throwError, of } from 'rxjs';
import { FieldValue } from '../../models/etrieveContent';
import { Attachment } from '../../models/attachment';
import { base64ToFile } from '../../utils/documentUtils';
import { Injectable, inject } from '@angular/core';

interface ParameterValue {
  parameterName: string;
  parameterCode: string;
  value: string;
}

interface Rule {
  name: string;
  code: string;
  parameterValues: ParameterValue[];
}

interface LookupType {
  code: string;
  name: string;
  keyfields: {
    name: string;
    code: string;
    dataType: string;
    isUniqueIdentifier: boolean;
    displayOrder: number;
  }[];
  namingFields: {
    fieldCode: string;
    required: boolean;
  }[];
}

export interface KeyField {
  code: string;
  dataType: string;
  name: string;
  displayOrder: number;
  rules: Rule[];
  lookupType?: LookupType;
  multiline?: boolean;
  textPattern?: string;
  default?: string;
  required?: boolean;
  maxLength?: number;
  multivalue?: boolean;
  minValue?: number;
  constraint: string[];
}

export interface DocumentType {
  name: string;
  code: string;
  areaName: string;
  areaCode: string;
  categoryName: string;
  categoryCode: string;
  keyfields: KeyField[];
}

export interface Area {
  name: string;
  code: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContentApiService {
  private http = inject(HttpClient);

  private baseUrl: string = '';

  /**
   *
   * @param url a url string that represents the baseUrl of the Softdocs content service
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * fetch a paged list of document types
   * @param requireAddPrivilege Whether to only return document types the user has add privileges for
   */
  getDocumentTypes(requireAddPrivilege: boolean = true): Observable<DocumentType[]> {
    const documentTypes: DocumentType[] = [];

    return this.fetchDocumentTypes(0, 100, documentTypes, requireAddPrivilege);
  }

  /**
   * fetch a list of document types
   * @param offset number of items to skip before collecting response items
   * @param limit page size
   * @param documentTypes a collective list of document types
   * @param requireAddPrivilege Whether to only return document types the user has add privileges for
   */
  private fetchDocumentTypes(
    offset: number,
    limit: number,
    documentTypes: DocumentType[],
    requireAddPrivilege: boolean = false
  ): Observable<DocumentType[]> {
    let params = new HttpParams().set('offset', offset.toString()).set('limit', limit.toString());

    if (requireAddPrivilege) {
      params = params.set('requireAddPrivilege', 'true');
    }

    const requestUrl = `${this.baseUrl}/api/documenttypes`;

    return this.http.get<DocumentType[]>(requestUrl, { params }).pipe(
      mergeMap(response => {
        documentTypes.push(...response);

        if (response.length === limit) {
          return this.fetchDocumentTypes(offset + limit, limit, documentTypes, requireAddPrivilege);
        } else {
          return [documentTypes];
        }
      }),
      catchError(throwError)
    );
  }

  /**
   * fetch a paged list of areas
   * @param requireAddPrivilege Whether to only return areas the user has add privileges for
   */
  getAreas(requireAddPrivilege: boolean = true): Observable<Area[]> {
    const areas: Area[] = [];

    return this.fetchAreas(0, 100, areas, requireAddPrivilege);
  }

  /**
   * fetch a list of areas
   * @param offset number of items to skip before collecting response items
   * @param limit page size
   * @param areas a collective list of areas
   * @param requireAddPrivilege Whether to only return areas the user has add privileges for
   */
  private fetchAreas(
    offset: number,
    limit: number,
    areas: Area[],
    requireAddPrivilege: boolean = false
  ): Observable<Area[]> {
    let params = new HttpParams().set('offset', offset.toString()).set('limit', limit.toString());

    if (requireAddPrivilege) {
      params = params.set('requireAddPrivilege', 'true');
    }

    const requestUrl = `${this.baseUrl}/api/areas`;

    return this.http.get<Area[]>(requestUrl, { params }).pipe(
      mergeMap(response => {
        areas.push(...response);

        if (response.length === limit) {
          return this.fetchAreas(offset + limit, limit, areas, requireAddPrivilege);
        } else {
          return [areas];
        }
      }),
      catchError(throwError)
    );
  }

  /**
   * Uploads a document to Etrieve
   * @param attachment the attachment to upload
   * @param area the area the attachment should be placed in
   * @param documentType the document type for the attachment
   * @param fieldValues key fields for the attachment
   */
  uploadDocument(
    attachment: Attachment,
    area: string,
    documentType: string,
    fieldValues: FieldValue[]
  ): Observable<any> {
    const body = {
      areaCode: area,
      documentTypeCode: documentType,
      fieldValues: fieldValues
    };

    return this.http.post<any>(`${this.baseUrl}/api/documents`, body).pipe(
      switchMap(metadataResponse => {
        const headers = new HttpHeaders({
          'Content-Type': attachment.attachmentType,
          'X-File-Attributes': JSON.stringify({
            Filename: attachment.name,
            ContentType: attachment.attachmentType
          })
        });

        const file = base64ToFile(
          attachment.content.content,
          attachment.name,
          attachment.attachmentType
        );

        return this.http
          .post<any>(`${this.baseUrl}/api/documents/${metadataResponse.id}/contents`, file, {
            headers: headers
          })
          .pipe(
            map(contentsResponse => ({
              metadataResponse,
              contentsResponse
            }))
          );
      })
    );
  }

  /**
   * Looks up users by name search and lookup type code
   * @param nameSearch  The name search string
   * @param lookupTypeCode  The lookup type code
   */
  lookupUser(nameSearch: string, lookupTypeCode: string): Observable<any> {
    const params = new HttpParams()
      .set('lookupTypeCode', lookupTypeCode)
      .set('nameSearchText', nameSearch);
    return this.http.get<any>(`${this.baseUrl}/api/lookups`, { params }).pipe(
      mergeMap(response => {
        return [response];
      })
    );
  }

  /**
   * Fetch a specific document type by its code
   * @param documentTypeCode The code of the document type to retrieve
   * @returns Observable of the requested DocumentType or null if not found
   */
  getDocumentTypeByCode(documentTypeCode: string): Observable<DocumentType | null> {
    if (!documentTypeCode) {
      return of(null);
    }

    const params = new HttpParams()
      .set('documenttypecode', documentTypeCode)
      .set('requireAddPrivilege', 'true');
    const requestUrl = `${this.baseUrl}/api/documenttypes`;

    return this.http.get<DocumentType[]>(requestUrl, { params }).pipe(
      map(response => {
        // If we got results and the first item matches our code, return it
        if (response && response.length > 0 && response[0].code === documentTypeCode) {
          return response[0];
        }
        return null;
      }),
      catchError(error => {
        console.error(`Error fetching document type with code ${documentTypeCode}:`, error);
        return of(null);
      })
    );
  }

  /**
   * Fetch document types by area code
   * @param areaCode The area code to filter document types
   * @param requireAddPrivilege Whether to only return document types the user has add privileges for
   */
  getDocumentTypesByArea(
    areaCode: string,
    requireAddPrivilege: boolean = true
  ): Observable<DocumentType[] | null> {
    return this.fetchDocumentTypesByArea(areaCode, 0, 100, [], requireAddPrivilege);
  }
  /**
   * Fetch a specific document type by its area code
   * @param areaCode The  areacode of the document type to retrieve
   * @param offset number of items to skip before collecting response items
   * @param limit page size
   * @param documentTypes a collective list of document types
   * @param requireAddPrivilege Whether to only return document types the user has add privileges for
   * @returns Observable of the requested DocumentType or null if not found
   */
  fetchDocumentTypesByArea(
    areaCode: string,
    offset: number,
    limit: number,
    documentTypes: DocumentType[],
    requireAddPrivilege: boolean = false
  ): Observable<DocumentType[] | null> {
    if (!areaCode) {
      return of(null);
    }

    let params = new HttpParams()
      .set('areaCode', areaCode)
      .set('offset', offset.toString())
      .set('limit', limit.toString());

    if (requireAddPrivilege) {
      params = params.set('requireAddPrivilege', 'true');
    }

    const requestUrl = `${this.baseUrl}/api/documenttypes`;

    return this.http.get<DocumentType[]>(requestUrl, { params }).pipe(
      mergeMap(response => {
        documentTypes.push(...response);

        if (response.length === limit) {
          return this.fetchDocumentTypesByArea(
            areaCode,
            offset + limit,
            limit,
            documentTypes,
            requireAddPrivilege
          );
        } else {
          return of(documentTypes);
        }
      }),
      catchError(error => {
        console.error(`Error fetching document types with area code ${areaCode}:`, error);
        return of(null);
      })
    );
  }
}
